/**
 * Vehicle control registry (handoff doc 06).
 *
 * Each module declares the VHAL properties it needs. At runtime the panel
 * normalises the emulator's property configs into a `PropertyCatalog`,
 * calls `match(catalog)` and lazy-loads only the modules whose
 * requirements are met. Modules read ranges/enums from the catalog; they
 * never hard-code them.
 */
import type { ComponentType } from "react";
import {
  VhalPropertyAccess,
  VhalPropertyChangeMode,
  VhalPropertyId,
  getPropertyInfo,
  isVendorProperty,
  type VhalPropertyConfig,
  type VhalPropertyValue,
} from "../cuttlefish/types/vhal";

export type CatalogSource = "device" | "static";

export type AreaConfig = {
  areaId: number;
  min?: number;
  max?: number;
  supportedValues?: number[];
};

export type CatalogEntry = {
  id: number;
  name: string;
  /** Android access mode allows writes (READ_WRITE / WRITE). */
  writable: boolean;
  /**
   * The panel can set it. Over gRPC the VHAL proxy writes straight into the
   * vehicle-side property store, so read-only props (gear, speed, ignition)
   * can be injected too; over ADB only Android-writable props can.
   */
  controllable: boolean;
  readable: boolean;
  changeMode: VhalPropertyChangeMode;
  areas: AreaConfig[];
  minSampleRate?: number;
  maxSampleRate?: number;
  source: CatalogSource;
  units?: string;
  enumValues?: Record<number, string>;
};

/** Keyed by property ID. */
export type PropertyCatalog = Map<number, CatalogEntry>;

/** Live values keyed by `${propertyId}_${areaId}`. */
export type PropertyValues = Map<string, VhalPropertyValue>;

export type Write = { prop: number; areaId?: number; value: VhalPropertyValue };
export type WriteResult = { ok: boolean; ms: number; errors: string[] };

export type PropRef = { prop: number; areas?: "any" | "all" };
export type Requirement = { all?: PropRef[]; any?: PropRef[]; anyWhere?: (e: CatalogEntry) => boolean };

/** Powertrain, from INFO_FUEL_TYPE (or property presence when it's absent). */
export type Powertrain = "ice" | "hybrid" | "ev" | "unknown";

export const POWERTRAIN_LABEL: Record<Powertrain, string> = {
  ice: "Combustion",
  hybrid: "Hybrid",
  ev: "Electric",
  unknown: "Unknown",
};

export type PowertrainState = {
  /** Effective powertrain modules are filtered by. */
  value: Powertrain;
  detected: Powertrain;
  /** Where `detected` came from. */
  source: "INFO_FUEL_TYPE" | "properties" | "none";
  overridden: boolean;
  /** Override the detected powertrain for this emulator; null restores auto. */
  set: (p: Powertrain | null) => void;
};

/** Panel sections, in display order. */
export const MODULE_GROUPS = [
  ["vehicle", "Vehicle"],
  ["powertrain", "Powertrain"],
  ["cabin", "Cabin"],
  ["body", "Body"],
  ["assist", "Driver assistance"],
  ["system", "System"],
] as const;
export type ModuleGroup = (typeof MODULE_GROUPS)[number][0];

export interface ModuleProps {
  catalog: PropertyCatalog;
  values: PropertyValues;
  write: (batch: Write[], label?: string) => Promise<WriteResult>;
  units: "metric" | "imperial";
  /** Mark a property as being dragged so stream values don't yank the control. */
  guard: (prop: number, areaId: number, on: boolean) => void;
  assets?: { sedanTopVertical?: string };
  powertrain: PowertrainState;
  /** Timeline event marker (scenario-style), e.g. "Engine started". */
  addEvent: (label: string) => void;
}

export interface VehicleModule {
  id: string;
  title: string;
  order: number;
  size: "sm" | "md" | "lg";
  group: ModuleGroup;
  /**
   * Whether the module's content may flow into columns when its cell is
   * wide (default true). Off for modules with their own wide layout.
   */
  reflow?: boolean;
  /** Fewest grid columns the module takes when the panel has them. */
  minSpan?: number;
  /** Shown only for these powertrains (all when omitted or unknown). */
  powertrains?: Powertrain[];
  requires: Requirement;
  optional?: PropRef[];
  load: () => Promise<{ default: ComponentType<ModuleProps> }>;
}

export type ModuleMatch = {
  module: VehicleModule;
  resolvedProps: number[];
  missingOptional: number[];
};

/* ------------------------------------------------------------------ */
/* Catalog                                                             */
/* ------------------------------------------------------------------ */

const P = VhalPropertyId;

export const propName = (id: number): string =>
  (P as unknown as Record<number, string>)[id] ?? `0x${id.toString(16)}`;

/** Normalise VHAL configs into a catalog. */
export function buildCatalog(
  configs: VhalPropertyConfig[],
  source: CatalogSource,
  /** gRPC proxy: vehicle-side writes, so every readable prop is controllable. */
  vehicleSide = false,
): PropertyCatalog {
  const out: PropertyCatalog = new Map();
  for (const c of configs) {
    const info = getPropertyInfo(c.propertyId);
    const areas: AreaConfig[] = (c.areaConfigs?.length ? c.areaConfigs : [{ areaId: 0 }]).map(
      (a) => {
        const min = a.minFloatValue || a.minInt32Value;
        const max = a.maxFloatValue || a.maxInt32Value;
        return {
          areaId: a.areaId,
          ...(min || max ? { min: min ?? 0, max: max ?? 0 } : {}),
          ...(a.supportedEnumValues?.length ? { supportedValues: a.supportedEnumValues } : {}),
        };
      },
    );
    const writable = c.access === VhalPropertyAccess.WRITE || c.access === VhalPropertyAccess.READ_WRITE;
    out.set(c.propertyId, {
      id: c.propertyId,
      name: propName(c.propertyId),
      writable,
      controllable: writable || (vehicleSide && c.access !== VhalPropertyAccess.NONE),
      readable: c.access === VhalPropertyAccess.READ || c.access === VhalPropertyAccess.READ_WRITE,
      changeMode: c.changeMode,
      areas,
      minSampleRate: c.minSampleRate,
      maxSampleRate: c.maxSampleRate,
      source,
      units: info.units,
      enumValues: info.enumValues,
    });
  }
  return out;
}

const has = (catalog: PropertyCatalog, ref: PropRef) => catalog.has(ref.prop);

/** FuelType values from the VHAL HIDL/AIDL spec. */
const FUEL_ELECTRIC = 10;
const FUEL_UNKNOWN = 0;

/**
 * INFO_FUEL_TYPE lists every fuel the vehicle takes: electric alone is an
 * EV, electric plus a liquid/gas fuel a hybrid, no electric a combustion
 * car. Without it, fall back on which fuel/battery props exist.
 */
export function detectPowertrain(
  catalog: PropertyCatalog,
  values: PropertyValues,
): { powertrain: Powertrain; source: PowertrainState["source"] } {
  const fuels = (values.get(valueKey(P.INFO_FUEL_TYPE))?.int32Values ?? []).filter((f) => f !== FUEL_UNKNOWN);
  if (fuels.length) {
    const electric = fuels.includes(FUEL_ELECTRIC);
    const other = fuels.some((f) => f !== FUEL_ELECTRIC);
    return { powertrain: electric ? (other ? "hybrid" : "ev") : "ice", source: "INFO_FUEL_TYPE" };
  }
  const ev = catalog.has(P.EV_BATTERY_LEVEL) || catalog.has(P.INFO_EV_BATTERY_CAPACITY);
  const ice = catalog.has(P.FUEL_LEVEL) || catalog.has(P.ENGINE_RPM);
  if (ev || ice) return { powertrain: ev && ice ? "hybrid" : ev ? "ev" : "ice", source: "properties" };
  return { powertrain: "unknown", source: "none" };
}

/**
 * Modules whose `requires` are satisfied by the catalog and whose
 * `powertrains` include the vehicle's, sorted by `order`.
 */
export function match(
  catalog: PropertyCatalog,
  modules: VehicleModule[] = MODULES,
  powertrain: Powertrain = "unknown",
): ModuleMatch[] {
  return modules
    .filter((m) => {
      if (m.powertrains && powertrain !== "unknown" && !m.powertrains.includes(powertrain)) return false;
      const { all, any, anyWhere } = m.requires;
      if (all && !all.every((r) => has(catalog, r))) return false;
      if (any && !any.some((r) => has(catalog, r))) return false;
      if (anyWhere && ![...catalog.values()].some(anyWhere)) return false;
      return Boolean(all || any || anyWhere);
    })
    .sort((a, b) => a.order - b.order)
    .map((module) => {
      const refs = [...(module.requires.all ?? []), ...(module.requires.any ?? []), ...(module.optional ?? [])];
      return {
        module,
        resolvedProps: refs.filter((r) => has(catalog, r)).map((r) => r.prop),
        missingOptional: (module.optional ?? []).filter((r) => !has(catalog, r)).map((r) => r.prop),
      };
    });
}

/* ------------------------------------------------------------------ */
/* Area helpers                                                        */
/* ------------------------------------------------------------------ */

export const SEAT = { R1L: 0x1, R1C: 0x2, R1R: 0x4, R2L: 0x10, R2C: 0x20, R2R: 0x40, R3L: 0x100, R3C: 0x200, R3R: 0x400 } as const;
export const DOOR = { FL: 0x1, FR: 0x4, RL: 0x10, RR: 0x40 } as const;
export const WHEEL = { FL: 0x1, FR: 0x2, RL: 0x4, RR: 0x8 } as const;

/** Human zone label for a (possibly combined) seat area. */
export function seatZoneLabel(areaId: number): string {
  if (areaId === 0) return "All";
  if (areaId & SEAT.R1L) return "Driver";
  if (areaId & SEAT.R1R) return "Passenger";
  if (areaId & (SEAT.R2L | SEAT.R2C | SEAT.R2R)) return "Rear";
  if (areaId & (SEAT.R3L | SEAT.R3C | SEAT.R3R)) return "Third row";
  return `0x${areaId.toString(16)}`;
}

export const valueKey = (prop: number, areaId = 0) => `${prop}_${areaId}`;

export function readNumber(values: PropertyValues, prop: number, areaId = 0): number | undefined {
  const v = values.get(valueKey(prop, areaId));
  return v?.floatValues?.[0] ?? v?.int32Values?.[0];
}

export function readBool(values: PropertyValues, prop: number, areaId = 0): boolean | undefined {
  const n = readNumber(values, prop, areaId);
  return n == null ? undefined : n !== 0;
}

export const intValue = (prop: number, areaId: number, n: number): Write => ({
  prop,
  areaId,
  value: { propertyId: prop, areaId, int32Values: [Math.round(n)] },
});
export const floatValue = (prop: number, areaId: number, n: number): Write => ({
  prop,
  areaId,
  value: { propertyId: prop, areaId, floatValues: [n] },
});
export const boolValue = (prop: number, areaId: number, b: boolean): Write => intValue(prop, areaId, b ? 1 : 0);

/* ------------------------------------------------------------------ */
/* Module set                                                          */
/* ------------------------------------------------------------------ */

/** Vite loads modules as ESM; the package's CJS typing would otherwise wrap `default`. */
const esm = (p: Promise<unknown>) => p as Promise<{ default: ComponentType<ModuleProps> }>;

const ADAS_RE = /^(LANE_|BLIND_SPOT|FORWARD_COLLISION|AUTOMATIC_EMERGENCY|EMERGENCY_LANE|DRIVER_ATTENTION|HANDS_ON|LOW_SPEED|CROSS_TRAFFIC|CRUISE_CONTROL|ADAPTIVE_CRUISE|ELECTRONIC_STABILITY)/;

export const isAdasProp = (e: CatalogEntry) =>
  /_ENABLED$/.test(e.name) && ADAS_RE.test(e.name) && !/^(CRUISE_CONTROL|ADAPTIVE_CRUISE|ELECTRONIC_STABILITY)/.test(e.name);

const ref = (...props: number[]): PropRef[] => props.map((prop) => ({ prop }));
const SEAT_RE = /^SEAT_/;
const DISPLAY_UNITS = [
  P.DISTANCE_DISPLAY_UNITS,
  P.FUEL_VOLUME_DISPLAY_UNITS,
  P.TIRE_PRESSURE_DISPLAY_UNITS,
  P.EV_BATTERY_DISPLAY_UNITS,
  P.VEHICLE_SPEED_DISPLAY_UNITS,
  P.HVAC_TEMPERATURE_DISPLAY_UNITS,
  P.FUEL_CONSUMPTION_UNITS_DISTANCE_OVER_VOLUME,
];
const LIGHTS = [
  P.HEADLIGHTS_STATE,
  P.HEADLIGHTS_SWITCH,
  P.HIGH_BEAM_LIGHTS_STATE,
  P.HIGH_BEAM_LIGHTS_SWITCH,
  P.FOG_LIGHTS_STATE,
  P.FOG_LIGHTS_SWITCH,
  P.FRONT_FOG_LIGHTS_STATE,
  P.FRONT_FOG_LIGHTS_SWITCH,
  P.REAR_FOG_LIGHTS_STATE,
  P.REAR_FOG_LIGHTS_SWITCH,
  P.HAZARD_LIGHTS_STATE,
  P.HAZARD_LIGHTS_SWITCH,
  P.TURN_SIGNAL_STATE,
  P.CABIN_LIGHTS_STATE,
  P.CABIN_LIGHTS_SWITCH,
  P.READING_LIGHTS_STATE,
  P.READING_LIGHTS_SWITCH,
  P.STEERING_WHEEL_LIGHTS_STATE,
  P.STEERING_WHEEL_LIGHTS_SWITCH,
  P.SEAT_FOOTWELL_LIGHTS_STATE,
  P.SEAT_FOOTWELL_LIGHTS_SWITCH,
];

/*
 * Registry. `group` places a module in a panel section, `powertrains`
 * hides it on vehicles it doesn't apply to (fuel on an EV, charging on a
 * combustion car). Each `load` is its own lazy chunk.
 */
export const MODULES: VehicleModule[] = [
  /* ---------------- Vehicle ---------------- */
  {
    id: "power",
    title: "Power",
    order: 10,
    size: "md",
    group: "vehicle",
    reflow: false,
    requires: { any: ref(P.IGNITION_STATE, P.AP_POWER_STATE_REPORT, P.VEHICLE_IN_USE) },
    optional: ref(P.AP_POWER_STATE_REQ, P.AP_POWER_BOOTUP_REASON, P.ENGINE_RPM, P.ENGINE_IDLE_AUTO_STOP_ENABLED, P.PARKING_BRAKE_ON),
    load: () => esm(import("./modules/power.js")),
  },
  {
    id: "drive",
    title: "Drive",
    order: 15,
    size: "md",
    group: "vehicle",
    reflow: false,
    requires: { any: [{ prop: P.GEAR_SELECTION }, { prop: P.CURRENT_GEAR }] },
    optional: [
      { prop: P.PERF_VEHICLE_SPEED },
      { prop: P.PARKING_BRAKE_ON },
      { prop: P.PARKING_BRAKE_AUTO_APPLY },
      { prop: P.PERF_STEERING_ANGLE },
    ],
    load: () => esm(import("./modules/drive.js")),
  },
  {
    id: "info",
    title: "Vehicle info",
    order: 18,
    size: "md",
    group: "vehicle",
    reflow: false,
    requires: { any: ref(P.INFO_MAKE, P.INFO_MODEL, P.INFO_VIN, P.INFO_FUEL_TYPE, P.INFO_EXTERIOR_DIMENSIONS) },
    optional: ref(
      P.INFO_MODEL_YEAR,
      P.INFO_FUEL_CAPACITY,
      P.INFO_EV_BATTERY_CAPACITY,
      P.INFO_EV_CONNECTOR_TYPE,
      P.INFO_FUEL_DOOR_LOCATION,
      P.INFO_EV_PORT_LOCATION,
      P.INFO_MULTI_EV_PORT_LOCATIONS,
      P.INFO_DRIVER_SEAT,
      P.VEHICLE_CURB_WEIGHT,
      P.TRAILER_PRESENT,
      P.GENERAL_SAFETY_REGULATION_COMPLIANCE_REQUIREMENT,
      P.VEHICLE_DRIVING_AUTOMATION_CURRENT_LEVEL,
    ),
    load: () => esm(import("./modules/info.js")),
  },

  /* ---------------- Powertrain ---------------- */
  {
    id: "powertrain",
    title: "Powertrain",
    order: 20,
    size: "md",
    group: "powertrain",
    requires: { any: ref(P.RANGE_REMAINING, P.PERF_ODOMETER, P.CURRENT_GEAR, P.WHEEL_TICK) },
    optional: ref(P.GEAR_SELECTION, P.PERF_VEHICLE_SPEED_DISPLAY, P.EV_REGENERATIVE_BRAKING_STATE, P.EV_STOPPING_MODE, P.EV_BRAKE_REGENERATION_LEVEL),
    load: () => esm(import("./modules/powertrain.js")),
  },
  {
    id: "engine",
    title: "Engine",
    order: 22,
    size: "md",
    group: "powertrain",
    powertrains: ["ice", "hybrid"],
    requires: { any: ref(P.ENGINE_RPM, P.ENGINE_COOLANT_TEMP, P.ENGINE_OIL_LEVEL, P.ENGINE_OIL_TEMP) },
    optional: ref(P.ENGINE_IDLE_AUTO_STOP_ENABLED),
    load: () => esm(import("./modules/engine.js")),
  },
  {
    id: "fuel",
    title: "Fuel",
    order: 24,
    size: "md",
    group: "powertrain",
    powertrains: ["ice", "hybrid"],
    requires: { any: ref(P.FUEL_LEVEL, P.INFO_FUEL_CAPACITY) },
    optional: ref(P.FUEL_LEVEL_LOW, P.FUEL_DOOR_OPEN, P.RANGE_REMAINING, P.INFO_FUEL_DOOR_LOCATION),
    load: () => esm(import("./modules/fuel.js")),
  },
  {
    id: "energy",
    title: "Battery & charging",
    order: 26,
    size: "md",
    group: "powertrain",
    powertrains: ["ev", "hybrid"],
    requires: { any: [{ prop: P.EV_BATTERY_LEVEL }, { prop: P.INFO_EV_BATTERY_CAPACITY }] },
    optional: [
      { prop: P.EV_CHARGE_PORT_OPEN },
      { prop: P.EV_CHARGE_PORT_CONNECTED },
      { prop: P.EV_CHARGE_PERCENT_LIMIT },
      { prop: P.EV_REGENERATIVE_BRAKING_STATE },
      { prop: P.EV_CURRENT_BATTERY_CAPACITY },
      { prop: P.EV_CHARGE_SWITCH },
      { prop: P.EV_CHARGE_STATE },
      { prop: P.EV_CHARGE_TIME_REMAINING },
      { prop: P.EV_BATTERY_INSTANTANEOUS_CHARGE_RATE },
      { prop: P.EV_BATTERY_AVERAGE_TEMPERATURE },
      { prop: P.EV_CHARGE_CURRENT_DRAW_LIMIT },
    ],
    load: () => esm(import("./modules/energy.js")),
  },

  /* ---------------- Cabin ---------------- */
  {
    id: "climate",
    title: "Climate",
    order: 30,
    size: "md",
    group: "cabin",
    reflow: false,
    requires: {
      any: [{ prop: P.HVAC_TEMPERATURE_SET }, { prop: P.HVAC_FAN_SPEED }, { prop: P.HVAC_POWER_ON }],
    },
    optional: [
      { prop: P.HVAC_AC_ON },
      { prop: P.HVAC_AUTO_ON },
      { prop: P.HVAC_RECIRC_ON },
      { prop: P.HVAC_DEFROSTER },
      { prop: P.HVAC_MAX_AC_ON },
      { prop: P.HVAC_DUAL_ON },
      { prop: P.HVAC_AUTO_RECIRC_ON },
      { prop: P.HVAC_FAN_DIRECTION },
      { prop: P.HVAC_FAN_DIRECTION_AVAILABLE },
      { prop: P.HVAC_STEERING_WHEEL_HEAT },
    ],
    load: () => esm(import("./modules/climate.js")),
  },
  {
    id: "seats",
    title: "Seats",
    order: 32,
    size: "md",
    group: "cabin",
    requires: { anyWhere: (e) => SEAT_RE.test(e.name) && !/LIGHTS/.test(e.name) },
    load: () => esm(import("./modules/seats.js")),
  },

  /* ---------------- Body ---------------- */
  {
    id: "body",
    title: "Body",
    order: 40,
    size: "md",
    group: "body",
    reflow: false,
    // Wireframe beside the door list instead of stacked above it.
    minSpan: 2,
    requires: { all: [{ prop: P.DOOR_POS, areas: "any" }] },
    optional: ref(P.DOOR_MOVE, P.DOOR_LOCK, P.DOOR_CHILD_LOCK_ENABLED, P.TIRE_PRESSURE, P.CRITICALLY_LOW_TIRE_PRESSURE, P.GLOVE_BOX_DOOR_POS, P.GLOVE_BOX_LOCKED),
    load: () => esm(import("./modules/body.js")),
  },
  {
    id: "lights",
    title: "Lights",
    order: 42,
    size: "md",
    group: "body",
    requires: { any: ref(...LIGHTS) },
    load: () => esm(import("./modules/lights.js")),
  },
  {
    id: "windows",
    title: "Windows & wipers",
    order: 44,
    size: "md",
    group: "body",
    requires: { any: ref(P.WINDOW_POS, P.WINDOW_LOCK, P.WINDSHIELD_WIPERS_SWITCH, P.WINDSHIELD_WIPERS_STATE) },
    optional: ref(P.WINDOW_MOVE, P.WINDSHIELD_WIPERS_PERIOD, P.HVAC_ELECTRIC_DEFROSTER_ON),
    load: () => esm(import("./modules/windows.js")),
  },
  {
    id: "mirrors",
    title: "Mirrors",
    order: 46,
    size: "sm",
    group: "body",
    requires: { any: ref(P.MIRROR_FOLD, P.MIRROR_LOCK, P.MIRROR_Y_POS, P.MIRROR_Z_POS, P.MIRROR_AUTO_FOLD_ENABLED) },
    optional: ref(P.MIRROR_AUTO_TILT_ENABLED, P.HVAC_SIDE_MIRROR_HEAT),
    load: () => esm(import("./modules/mirrors.js")),
  },
  {
    id: "steering",
    title: "Steering wheel",
    order: 48,
    size: "sm",
    group: "body",
    requires: {
      any: ref(P.STEERING_WHEEL_HEIGHT_POS, P.STEERING_WHEEL_DEPTH_POS, P.STEERING_WHEEL_LOCKED, P.STEERING_WHEEL_THEFT_LOCK_ENABLED, P.PERF_STEERING_ANGLE),
    },
    optional: ref(P.STEERING_WHEEL_EASY_ACCESS_ENABLED, P.PERF_REAR_STEERING_ANGLE, P.HVAC_STEERING_WHEEL_HEAT),
    load: () => esm(import("./modules/steering.js")),
  },

  /* ---------------- Driver assistance ---------------- */
  {
    id: "adas",
    title: "Driver assistance",
    order: 50,
    size: "md",
    group: "assist",
    requires: { anyWhere: isAdasProp },
    load: () => esm(import("./modules/adas.js")),
  },
  {
    id: "cruise",
    title: "Cruise control",
    order: 52,
    size: "sm",
    group: "assist",
    requires: { any: ref(P.CRUISE_CONTROL_ENABLED, P.CRUISE_CONTROL_STATE, P.CRUISE_CONTROL_TARGET_SPEED) },
    optional: ref(
      P.CRUISE_CONTROL_TYPE,
      P.CRUISE_CONTROL_COMMAND,
      P.ADAPTIVE_CRUISE_CONTROL_TARGET_TIME_GAP,
      P.ADAPTIVE_CRUISE_CONTROL_LEAD_VEHICLE_MEASURED_DISTANCE,
    ),
    load: () => esm(import("./modules/cruise.js")),
  },
  {
    id: "safety",
    title: "Safety status",
    order: 54,
    size: "sm",
    group: "assist",
    requires: {
      any: ref(P.ABS_ACTIVE, P.TRACTION_CONTROL_ACTIVE, P.ELECTRONIC_STABILITY_CONTROL_STATE, P.IMPACT_DETECTED, P.SEAT_AIRBAGS_DEPLOYED),
    },
    optional: ref(P.SEAT_BELT_PRETENSIONER_DEPLOYED, P.CRITICALLY_LOW_TIRE_PRESSURE),
    load: () => esm(import("./modules/safety.js")),
  },

  /* ---------------- System ---------------- */
  {
    id: "environment",
    title: "Environment",
    order: 60,
    size: "sm",
    group: "system",
    requires: { any: ref(P.ENV_OUTSIDE_TEMPERATURE, P.NIGHT_MODE, P.ANDROID_EPOCH_TIME) },
    load: () => esm(import("./modules/environment.js")),
  },
  {
    id: "display",
    title: "Display & units",
    order: 62,
    size: "md",
    group: "system",
    requires: { any: ref(P.DISPLAY_BRIGHTNESS, P.HEAD_UP_DISPLAY_ENABLED, P.VALET_MODE_ENABLED, ...DISPLAY_UNITS) },
    load: () => esm(import("./modules/display.js")),
  },
  {
    id: "keys",
    title: "Steering wheel keys",
    order: 64,
    size: "sm",
    group: "system",
    requires: { any: ref(P.HW_KEY_INPUT, P.HW_ROTARY_INPUT, P.HW_CUSTOM_INPUT) },
    load: () => esm(import("./modules/keys.js")),
  },
];
